import { GET_MARKETPLACES } from '@/housefolios-components/Marketplace/queries'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'

export const MARKETPLACE_PROPERTIES_QUERY_NAME = 'marketplaceProperties'

function marketplaceUrls(activeOrg) {
  return [
    ...(activeOrg?.marketPlaces || []),
    ...(activeOrg?.sharedMarketplaces || []),
  ]
    .map((marketplace) => marketplace?.url)
    .filter(Boolean)
}

/**
 * Apollo refetchQueries for marketplace card/list views.
 *
 * The string name refetches every active `marketplaceProperties` query with
 * its original variables (including `limit`). Explicit GET_MARKETPLACES
 * objects also include `limit` so cached marketplace pages stay in sync.
 */
export function propertyIdSet(propertyIds) {
  return new Set((propertyIds || []).filter(Boolean).map(String))
}

export function shouldStripSubmittedMarketplaceQuery(args) {
  if (!args) return true
  return args.approved === false
}

export function filterMarketplacePropertiesList(
  existing,
  propertyIds,
  readField,
) {
  const properties = existing?.properties
  if (!Array.isArray(properties)) return existing

  const ids = propertyIdSet(propertyIds)
  const nextProperties = properties.filter((ref) => {
    const id = readField('_id', ref)
    return !ids.has(String(id))
  })

  if (nextProperties.length === properties.length) return existing

  const removed = properties.length - nextProperties.length
  const totalCount =
    typeof existing.totalCount === 'number'
      ? Math.max(0, existing.totalCount - removed)
      : existing.totalCount

  return {
    ...existing,
    properties: nextProperties,
    totalCount,
  }
}

export function removeSubmittedMarketplaceProperties(cache, propertyIds) {
  if (!propertyIds?.length || !cache?.modify) return

  cache.modify({
    fields: {
      marketplaceProperties(existing, { readField, args  }) {
        if (!shouldStripSubmittedMarketplaceQuery(args)) return existing
        return filterMarketplacePropertiesList(
          existing,
          propertyIds,
          readField,
        )
      },
    },
  })
}

export function approvalMutationCacheOptions(fieldName, propertyIds) {
  return {
    optimisticResponse: {
      __typename: 'Mutation',
      [fieldName]: (propertyIds || []).map((id) => ({
        __typename: 'Property',
        _id: id,
      })),
    },
    update(cache, { data  }) {
      if (!data?.[fieldName]) return
      removeSubmittedMarketplaceProperties(cache, propertyIds)
    },
    refetchQueries: [MARKETPLACE_PROPERTIES_QUERY_NAME],
    awaitRefetchQueries: false,
  }
}

/**
 * Apollo refetchQueries for marketplace card/list views.
 *
 * The string name refetches every active `marketplaceProperties` query with
 * its original variables (including `limit`). Explicit GET_MARKETPLACES
 * objects also include `limit` so cached marketplace pages stay in sync.
 */
export function marketplaceListRefetchQueries(activeOrg) {
  const orgId = activeOrg?._id
  const urls = marketplaceUrls(activeOrg)

  return [
    MARKETPLACE_PROPERTIES_QUERY_NAME,
    {
      query: GET_MARKETPLACES,
      variables: {
        marketplaces: ['housefolios', 'myGrayLabel'],
        approved: true,
        limit: GET_PROPERTIES_QUERY_LIMIT,
      },
    },
    orgId
      ? {
          query: GET_MARKETPLACES,
          variables: {
            marketplaces: ['housefolios', 'myGrayLabel'],
            orgIds: [orgId],
            approved: true,
            limit: GET_PROPERTIES_QUERY_LIMIT,
          },
        }
      : null,
    ...urls.map((url) => ({
      query: GET_MARKETPLACES,
      variables: {
        marketplaces: [url],
        approved: true,
        limit: GET_PROPERTIES_QUERY_LIMIT,
      },
    })),
  ].filter(Boolean)
}
